import type { Achievement, UserProps } from "../hooks/auth";
import { ACHIEVEMENTS } from "../config/achievements";
import { weeklyLeaguesService, XP_VALUES } from "./weeklyLeaguesService";

export const LEVEL_POINTS = [
  0, 100, 250, 450, 700, 1000, 1350, 1750, 2200, 2700, 3250, 3850, 4500, 5200,
  5950, 6750, 7600, 8500, 9450, 10450,
];

export const ACTION_POINTS = {
  VIEW_WORD: 5,
  SEARCH: 5,
  FAVORITE: 5,
  DAILY_LOGIN: 20,
  UPLOAD_VIDEO: 50,
  UPLOAD_PROFILE_PHOTO: 5,
  GAME_MEMORY: 10,
  QUIZ_DATILOLOGIA: 15,
} as const;

export type GamificationAction = keyof typeof ACTION_POINTS;

type AchievementDef = (typeof ACHIEVEMENTS)[keyof typeof ACHIEVEMENTS];

function toUnlocked(def: AchievementDef): Achievement {
  return {
    id: def.id,
    name: def.name,
    description: def.description,
    icon: def.icon,
    points: def.points,
    unlockedAt: new Date().toISOString(),
  };
}

export const gamificationService = {
  calculateLevel(points: number): number {
    for (let i = LEVEL_POINTS.length - 1; i >= 0; i -= 1) {
      if (points >= LEVEL_POINTS[i]) {
        return i + 1;
      }
    }
    return 1;
  },

  getPointsToNextLevel(points: number): number {
    const currentLevel = this.calculateLevel(points);
    if (currentLevel >= LEVEL_POINTS.length) {
      return 0;
    }
    return LEVEL_POINTS[currentLevel] - points;
  },

  getLevelProgressPercent(points: number): number {
    const level = this.calculateLevel(points);
    if (level >= LEVEL_POINTS.length) return 100;
    const floor = LEVEL_POINTS[level - 1] ?? 0;
    const ceil = LEVEL_POINTS[level] ?? floor + 1;
    const span = ceil - floor || 1;
    return Math.min(100, Math.max(0, ((points - floor) / span) * 100));
  },

  checkAchievements(user: UserProps): Achievement[] {
    const newAchievements: Achievement[] = [];
    const currentIds = (user.achievements || []).map((a) => a.id);
    const pushIf = (condition: boolean, def: AchievementDef) => {
      if (condition && !currentIds.includes(def.id)) {
        newAchievements.push(toUnlocked(def));
      }
    };

    const words = user.totalWordsViewed || 0;
    const searches = user.totalSearches || 0;
    const favorites = user.totalFavorites || 0;
    const streak = user.streakDays || 0;
    const videos = user.videoCount || 0;
    const level = this.calculateLevel(user.points || 0);

    pushIf(words >= 1, ACHIEVEMENTS.FIRST_WORD);
    pushIf(words >= 10, ACHIEVEMENTS.WORD_MASTER_10);
    pushIf(words >= 50, ACHIEVEMENTS.WORD_MASTER_50);
    pushIf(words >= 100, ACHIEVEMENTS.WORD_MASTER_100);
    pushIf(searches >= 1, ACHIEVEMENTS.FIRST_SEARCH);
    pushIf(searches >= 10, ACHIEVEMENTS.SEARCH_MASTER_10);
    pushIf(searches >= 50, ACHIEVEMENTS.SEARCH_MASTER_50);
    pushIf(favorites >= 1, ACHIEVEMENTS.FIRST_FAVORITE);
    pushIf(favorites >= 10, ACHIEVEMENTS.FAVORITE_MASTER_10);
    pushIf(favorites >= 50, ACHIEVEMENTS.FAVORITE_MASTER_50);
    pushIf(streak >= 7, ACHIEVEMENTS.STREAK_7);
    pushIf(streak >= 30, ACHIEVEMENTS.STREAK_30);
    pushIf(videos >= 1, ACHIEVEMENTS.FIRST_VIDEO);
    pushIf(videos >= 5, ACHIEVEMENTS.VIDEO_MASTER_5);
    pushIf(level >= 5, ACHIEVEMENTS.LEVEL_5);
    pushIf(level >= 10, ACHIEVEMENTS.LEVEL_10);
    pushIf(level >= 20, ACHIEVEMENTS.LEVEL_20);

    return newAchievements;
  },

  updateStreak(user: UserProps): number {
    const today = new Date().toDateString();
    const lastActivity = user.lastActivityDate
      ? new Date(user.lastActivityDate).toDateString()
      : null;

    if (lastActivity === today) {
      return user.streakDays || 0;
    }

    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();
    if (lastActivity === yesterday) {
      return (user.streakDays || 0) + 1;
    }

    return 1;
  },

  hasEarnedDailyLoginToday(user: UserProps): boolean {
    if (!user.lastLogin) return false;
    const today = new Date().toDateString();
    return new Date(user.lastLogin).toDateString() === today;
  },

  async addPoints(
    user: UserProps,
    action: GamificationAction,
    updateUserProfile: (
      updates: Partial<UserProps>,
      options?: { skipSuccessToast?: boolean }
    ) => Promise<void>
  ): Promise<{ newAchievements: Achievement[]; pointsGained: number }> {
    if (!user?.uid) {
      return { newAchievements: [], pointsGained: 0 };
    }

    let pointsGained = ACTION_POINTS[action];

    if (action === "DAILY_LOGIN" && this.hasEarnedDailyLoginToday(user)) {
      return { newAchievements: [], pointsGained: 0 };
    }

    const newPoints = (user.points || 0) + pointsGained;
    const newLevel = this.calculateLevel(newPoints);
    const newStreak = this.updateStreak(user);

    const updates: Partial<UserProps> = {
      points: newPoints,
      level: newLevel,
      streakDays: newStreak,
      lastActivityDate: new Date().toISOString(),
    };

    let leagueXp = 0;

    switch (action) {
      case "VIEW_WORD":
        updates.totalWordsViewed = (user.totalWordsViewed || 0) + 1;
        leagueXp = XP_VALUES.CONSULTA_SIMPLES;
        break;
      case "SEARCH":
        updates.totalSearches = (user.totalSearches || 0) + 1;
        leagueXp = XP_VALUES.CONSULTA_SIMPLES;
        break;
      case "FAVORITE":
        updates.totalFavorites = (user.totalFavorites || 0) + 1;
        leagueXp = XP_VALUES.CONSULTA_SIMPLES;
        break;
      case "DAILY_LOGIN":
        updates.lastLogin = Date.now();
        leagueXp = XP_VALUES.REVISAO_DIARIA;
        break;
      case "UPLOAD_VIDEO":
        leagueXp = XP_VALUES.GRAVACAO_VIDEO;
        break;
      case "UPLOAD_PROFILE_PHOTO":
        leagueXp = XP_VALUES.CONSULTA_SIMPLES;
        break;
      case "GAME_MEMORY":
        leagueXp = XP_VALUES.JOGO_MEMORIA_ACERTO;
        break;
      case "QUIZ_DATILOLOGIA":
        leagueXp = XP_VALUES.QUIZ_DATILOLOGIA;
        break;
      default:
        break;
    }

    await updateUserProfile(updates, { skipSuccessToast: true });

    if (leagueXp > 0) {
      try {
        await weeklyLeaguesService.addXP(
          user.uid,
          user.displayName || user.name || "Usuário",
          user.photoURL || user.avatar,
          leagueXp
        );
      } catch {
        // não bloquear fluxo
      }
    }

    const updatedUser = { ...user, ...updates };
    const newAchievements = this.checkAchievements(updatedUser);

    if (newAchievements.length > 0) {
      const achievementPoints = newAchievements.reduce(
        (sum, a) => sum + a.points,
        0
      );
      const finalPoints = newPoints + achievementPoints;
      await updateUserProfile(
        {
          points: finalPoints,
          level: this.calculateLevel(finalPoints),
          achievements: [...(user.achievements || []), ...newAchievements],
        },
        { skipSuccessToast: true }
      );
      pointsGained += achievementPoints;
    }

    return { newAchievements, pointsGained };
  },
};
